import { useCallback, useEffect, useState } from "react"

import Toast from "@/components/Toast"
import { i18n } from "@/i18n"
import { completeShare, getDrawInfo } from "@/services/core"
import { useRoleInfo } from "@/user/session"
import { collectSocialMediaShare } from "@/utils/event"
import { adapter } from "@/utils/sdk"

const SHARE_CHANNELS = ["download", "twitter", "copy"]

export const getShareUrl = (options?: {
    isRecall?: boolean
    uid?: string
    code?: string
    shareType?: string
}) => {
    const { isRecall, uid, code } = options || {}

    const url = new URL(location.origin + location.pathname)
    if (isRecall) {
        url.searchParams.set("page", "recall")
    }
    if (code) {
        url.searchParams.set("invite_code", code)
    }
    if (options?.shareType) {
        url.searchParams.set("share_type", options.shareType)
    }
    url.searchParams.set("source_from", adapter.source.from || "")
    url.searchParams.set("share_by", uid ? window.btoa(String(uid)) : "")
    return url.toString()
}

export const drawQRCode = async (uid?: string, isRecall?: boolean) => {
    const { default: QRCode } = await import("qrcode")
    const url = getShareUrl({ uid, shareType: "qrCode", isRecall })
    const canvas = document.createElement("canvas")
    await QRCode.toCanvas(canvas, url, {
        errorCorrectionLevel: "L",
        width: 268,
        margin: 3,
    })
    return canvas
}

const onShareSuccess = (isRecall: boolean) =>
    collectSocialMediaShare(isRecall ? "recall" : "game", "save_as_image")

interface UseShareChannelsProps {
    imageSrc?: string
    imageRef?: React.RefObject<HTMLImageElement>
    download: () => void
    isRecall: boolean
    code?: string
    onShareComplete?: () => void
    onFirstMainShare?: () => void
}

export const useShareChannels = (props: UseShareChannelsProps) => {
    const { imageSrc, download, isRecall, onShareComplete, onFirstMainShare } = props

    const user = useRoleInfo()
    const uid = user?.uid

    const [channels, setChannels] = useState<string[]>([])

    useEffect(() => {
        setChannels(SHARE_CHANNELS)
    }, [])

    const onClickChannel = useCallback(
        async (key: string) => {

            if (!isRecall) {
                ;(async () => {
                    const shareRes = await completeShare("main")
                    if (shareRes.code !== 0) return
                    const drawRes = await getDrawInfo()
                    if (drawRes.code !== 0) return
                    const coupon = drawRes.data?.coupons.find(
                        (c) => c.cid === __MAIN_SHARE_COUPON_CID
                    )
                    if (!coupon?.result?.won) {
                        onFirstMainShare?.()
                    }
                })()
            } else {
                completeShare("recall").then((res) => {
                    if (res.code === 0) {
                        onShareComplete?.()
                    }
                })
            }

            if (key === "download") {
                try {
                    download()
                    onShareSuccess(isRecall)
                } catch {
                    Toast.message(i18n.get("share.messages.shareFailed"))
                }
                return
            }

            if (key === "twitter") {
                const shareUrl = `${window.location.origin}${window.location.pathname}`
                window.open(
                    "https://twitter.com/intent/tweet?url=" +
                        encodeURIComponent(`「足跡」——アークナイツ特設WEBキャンペーン
${shareUrl}
＃アークナイツ　＃足跡`),
                    "_blank",
                )
                return
            }

            if (key === "facebook") {
                const shareUrl = `${window.location.origin}${window.location.pathname}`
                window.open(
                    "https://www.facebook.com/sharer/sharer.php?u=" + shareUrl,
                    "_blank",
                )
                return
            }

            if (key === "copy") {
                try {
                    const url = getShareUrl({ isRecall, uid })
                    await navigator.clipboard.writeText(url)
                    Toast.message(i18n.get("msg.copySuccess"))
                } catch {
                    Toast.message(i18n.get("share.messages.copyFailed"))
                }
            }
        },
        [download, isRecall, uid, onShareComplete, onFirstMainShare],
    )

    return { channels, onClickChannel }
}
